import React from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import { Pagination, MenuItem, Select } from "@mui/material";

/**
 * PaginationBar - Reusable pagination controls for tables/lists
 * Props:
 * - currentPage (number)
 * - rowsPerPage (number)
 * - totalItems (number)
 * - totalPages (number, optional) -> if omitted, computed from totalItems/rowsPerPage
 * - onPageChange ((event, page) => void)
 * - onRowsPerPageChange ((event) => void)
 * - pageSizeOptions (number[], optional)
 */
const PaginationBar = ({
  currentPage,
  rowsPerPage,
  totalItems,
  totalPages,
  onPageChange,
  onRowsPerPageChange,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  const pageCount = totalPages || Math.ceil((totalItems || 0) / (rowsPerPage || 1)) || 0;
  const start = totalItems > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const end = Math.min(currentPage * rowsPerPage, totalItems || 0);

  return (
    <ArgonBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={3}
      flexWrap="wrap"
      gap={2}
    >
      {/* Rows per page selector */}
      <ArgonBox display="flex" alignItems="center" gap={1}>
        <ArgonTypography
          variant="button"
          color="text"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "#6b7280",
            whiteSpace: "nowrap",
          }}
        >
          Rows per page:
        </ArgonTypography>
        <Select
          value={rowsPerPage}
          onChange={onRowsPerPageChange}
          size="small"
          sx={{
            height: 34,
            minWidth: 84,
            borderRadius: 1,
            "& .MuiSelect-select": {
              py: 0.5,
              px: 1,
            },
          }}
        >
          {pageSizeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </ArgonBox>

      {/* Showing info */}
      <ArgonTypography
        variant="button"
        color="text"
        sx={{ textTransform: "uppercase", letterSpacing: 0.8, color: "#6b7280" }}
      >
        Showing {start} to {end} of {totalItems || 0} entries
      </ArgonTypography>

      {/* Pagination */}
      <Pagination
        count={pageCount}
        page={currentPage}
        onChange={onPageChange}
        color="primary"
        shape="rounded"
        showFirstButton
        showLastButton
        sx={{
          "& .MuiPaginationItem-root": {
            fontWeight: 500,
            color: "#6b7280",
          },
          "& .MuiPaginationItem-root.Mui-selected": {
            backgroundColor: "#6366F1",
            color: "#fff",
            borderRadius: "8px",
          },
          "& .MuiPaginationItem-root.Mui-disabled": {
            opacity: 0.4,
          },
        }}
      />
    </ArgonBox>
  );
};

export default PaginationBar;

PaginationBar.propTypes = {
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};

PaginationBar.defaultProps = {
  totalItems: 0,
  totalPages: undefined,
  pageSizeOptions: [10, 25, 50, 100],
};

